import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash';

import Moment from 'moment';
import { extendMoment } from 'moment-range';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import RootRef from '@material-ui/core/RootRef';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import TextField from 'components/fields/TextField';
import IconCalendarStart from 'components/icons/IconCalendarStart';
import IconCalendarEnd from 'components/icons/IconCalendarEnd';
import IconCross from 'components/icons/IconCross';

import defaultDateFormat from 'config/dateFormat';
import isDescendant from 'utils/isDescendant';

import Datepicker from './Datepicker';

const moment = extendMoment(Moment);

function displayValueToIso(displayValue, dateFormat) {
  if (displayValue == null || displayValue === '') {
    return '';
  }
  const parsedDate = moment(displayValue, dateFormat, true);
  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
}

function isoValueToDisplay(isoValue, dateFormat) {
  if (isoValue == null || isoValue === '') {
    return '';
  }
  const parsedDate = moment(isoValue, 'YYYY-MM-DD', true);
  return parsedDate.isValid() ? parsedDate.format(dateFormat) : '';
}

function boundaryToIso(boundaryValue, dateFormat) {
  if (boundaryValue == null || boundaryValue === '') {
    return '';
  }
  if (moment.isMoment(boundaryValue)) {
    return boundaryValue.isValid() ? boundaryValue.format('YYYY-MM-DD') : '';
  }
  if (boundaryValue instanceof Date) {
    const parsedDate = moment(boundaryValue);
    return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
  }
  if (moment(boundaryValue, 'YYYY-MM-DD', true).isValid()) {
    return moment(boundaryValue, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
  }
  if (moment(boundaryValue, dateFormat, true).isValid()) {
    return moment(boundaryValue, dateFormat, true).format('YYYY-MM-DD');
  }
  return '';
}

const styles = theme => ({
  root: {
    position: 'relative',
  },
  formControl: {
    display: 'flex',
  },
  popperRoot: {
    position: 'absolute',
    top: 0,
    left: 0,
    margin: 0,
    transform: 'scale(0, 0)',
    zIndex: '1200',
  },
  textFields: {
    position: 'relative',
    display: 'flex',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: 10,
      top: 1,
      bottom: 1,
      left: '50%',
      marginLeft: -5,
      zIndex: 1,
    },
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: 8,
      height: 1,
      top: '50%',
      left: '50%',
      marginLeft: -4,
      marginTop: -1,
      zIndex: 1,
      backgroundColor: theme.palette.text.primary,
    },
  },
  textFieldsColorLight: {
    '&:before': {
      backgroundColor: theme.palette.common.white,
    },
  },
  textField: {
    display: 'flex',
    width: 'calc(50% + 5px)',
    marginLeft: -5,
    '& input': {
      paddingLeft: 10,
      paddingRight: 5,
    },
    '&:first-child': {
      marginLeft: 0,
      '& input': {
        paddingLeft: 10,
        paddingRight: 5,
      },
      '& $textFieldInputRoot': {
        borderRight: 'none',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
    '&:last-child': {
      '& $textFieldInputRoot': {
        borderLeft: 'none',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
    },
  },
  textFieldInputRoot: {
    display: 'flex',
  },
  inputAdornmentinputAdornmentLgOnly: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
    },
  },
  iconCalendarStart: {
    width: 14,
    height: 16,
    marginTop: -2,
    color: theme.palette.grey[600],
  },
  iconCalendarStartSizeSm: {
    marginLeft: -3,
    marginRight: -3,
  },
  iconCalendarEnd: {
    width: 14,
    height: 16,
    marginTop: -2,
    color: theme.palette.grey[600],
  },
  iconCalendarEndSizeSm: {
    marginLeft: -3,
    marginRight: -3,
  },
  mobileNativeOverlayWrapper: {
    position: 'relative',
  },
  mobileNativeDateInputOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0.01,
    zIndex: 2,
    border: 0,
    margin: 0,
    padding: 0,
    cursor: 'pointer',
  },
  labelOverlap: {
    position: 'relative',
    marginBottom: -6,
    marginLeft: 4,
    width: 'fit-content',
    zIndex: 2,
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      right: -1,
      left: -1,
      bottom: 5,
      height: 2,
      backgroundColor: theme.palette.common.white,
      zIndex: -1,
    },
  },
  buttonClear: {
    margin: '-18px -22px -18px -18px',
  },
  iconCross: {
    width: 12,
    height: 12,
  },
});

class DatepickerField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      datepickerOpen: false,
      disableDatepickerTabFocus: false,
      focusedField: null,
    };

    this.onTextFieldFocus = this.onTextFieldFocus.bind(this);
    this.onTextFieldBlur = this.onTextFieldBlur.bind(this);
    this.onStartTextFieldBlur = this.onStartTextFieldBlur.bind(this);
    this.onEndTextFieldBlur = this.onEndTextFieldBlur.bind(this);
    this.onTextFieldChange = this.onTextFieldChange.bind(this);
    this.onStartTextFieldChange = this.onStartTextFieldChange.bind(this);
    this.onEndTextFieldChange = this.onEndTextFieldChange.bind(this);
    this.onTextFieldKeyDown = this.onTextFieldKeyDown.bind(this);
    this.onDatepickerClickAway = this.onDatepickerClickAway.bind(this);
    this.onDatepickerBlur = this.onDatepickerBlur.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.onStartDateChange = this.onStartDateChange.bind(this);
    this.onEndDateChange = this.onEndDateChange.bind(this);
    this.onNativeDefaultChange = this.onNativeDefaultChange.bind(this);
    this.onNativeStartChange = this.onNativeStartChange.bind(this);
    this.onNativeEndChange = this.onNativeEndChange.bind(this);
    this.onNativeDefaultBlur = this.onNativeDefaultBlur.bind(this);
    this.onNativeStartBlur = this.onNativeStartBlur.bind(this);
    this.onNativeEndBlur = this.onNativeEndBlur.bind(this);
    this.onDatepickerOpen = this.onDatepickerOpen.bind(this);
    this.onDatepickerClose = this.onDatepickerClose.bind(this);
    this.onClearButtonClick = this.onClearButtonClick.bind(this);

    this.justOpenedDatepicker = false;
    this.justClosedDatepicker = false;
    this.timeoutDelay = 300;
  }

  componentWillReceiveProps(nextProps) {
    switch (nextProps.variant) {
      case 'range': {
        const { startDateName, endDateName } = this.props;
        const endFieldProps = get(nextProps, endDateName);
        const startFieldProps = get(nextProps, startDateName);
        if (endFieldProps.meta.active
        && endFieldProps.meta.valid
        && document.activeElement === this.endInputRef) {
          this.setState({ disableDatepickerTabFocus: true });
        } else {
          this.setState({ disableDatepickerTabFocus: false });
        }
        if (startFieldProps.meta.active) {
          this.setState({ focusedField: 'start' });
        }
        if (endFieldProps.meta.active) {
          this.setState({ focusedField: 'end' });
        }
        break;
      }
      default: {
        if (nextProps.meta.active
          && nextProps.meta.valid
          && document.activeElement === this.startInputRef) {
          this.setState({ disableDatepickerTabFocus: true });
        } else {
          this.setState({ disableDatepickerTabFocus: false });
        }
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.closeDatepickerTimeout);
    clearTimeout(this.openDatepickerTimeout);
  }

  isMobileLayout() {
    const { width } = this.props;
    return width === 'xs' || width === 'sm';
  }

  onDatepickerOpen() {
    if (this.isMobileLayout()) {
      return;
    }
    this.justOpenedDatepicker = true;
    this.openDatepickerTimeout = setTimeout(() => {
      this.justOpenedDatepicker = false;
    }, this.timeoutDelay);
    this.setState({ datepickerOpen: true });
  }

  onDatepickerClose() {
    this.justClosedDatepicker = true;
    this.closeDatepickerTimeout = setTimeout(() => {
      this.justClosedDatepicker = false;
    }, this.timeoutDelay);
    this.setState({
      datepickerOpen: false,
      focusedField: null,
    });
  }

  onTextFieldFocus(event) {
    if (!this.justClosedDatepicker) {
      this.props.onInputFocus(event);
      if (!this.isMobileLayout()) {
        this.onDatepickerOpen();
      }
    }
  }

  onTextFieldBlur() {
    const { input } = this.props;
    setTimeout(() => {
      const datepickerFocused = document.activeElement === document.body
        || (this.datepickerRef
        && isDescendant(this.datepickerRef, document.activeElement));
      if (!datepickerFocused) {
        input.onBlur();
        this.onDatepickerClose();
      }
    }, this.timeoutDelay);
  }

  onStartTextFieldBlur() {
    const startFieldProps = this.startFieldProps;
    setTimeout(() => {
      const datepickerFocused = document.activeElement === document.body
        || (this.endInputRef && document.activeElement === this.endInputRef)
        || (this.datepickerRef
        && isDescendant(this.datepickerRef, document.activeElement));
      if (!datepickerFocused) {
        startFieldProps.input.onBlur();
        this.onDatepickerClose();
      }
    }, this.timeoutDelay);
  }

  onEndTextFieldBlur() {
    const endFieldProps = this.endFieldProps;
    setTimeout(() => {
      const datepickerFocused = document.activeElement === document.body
        || (this.startInputRef && document.activeElement === this.startInputRef)
        || (this.datepickerRef
        && isDescendant(this.datepickerRef, document.activeElement));
      if (!datepickerFocused) {
        endFieldProps.input.onBlur();
        this.onDatepickerClose();
      }
    }, this.timeoutDelay);
  }

  onTextFieldChange(event) {
    if (!this.justClosedDatepicker) {
      this.onDatepickerOpen();
    }
    this.props.input.onChange(event);
  }

  onStartTextFieldChange(event) {
    const { startDateName, ...otherProps } = this.props;
    const startFieldProps = this.startFieldProps;
    const value =
    otherProps.normalize && otherProps.normalize[startDateName]
      ? otherProps.normalize[startDateName](event.target.value)
      : event.target.value;
    startFieldProps.input.onChange(value);
    if (!this.justClosedDatepicker) {
      this.onDatepickerOpen();
    }
  }

  onEndTextFieldChange(event) {
    const { endDateName, ...otherProps } = this.props;
    const endFieldProps = this.endFieldProps;
    const value =
    otherProps.normalize && otherProps.normalize[endDateName]
      ? otherProps.normalize[endDateName](event.target.value)
      : event.target.value;
    endFieldProps.input.onChange(value);
    if (!this.justClosedDatepicker) {
      this.onDatepickerOpen();
    }
  }

  onTextFieldKeyDown(event) {
    if (event.key === 'ArrowDown') {
      const onArrowDownClick = () => {
        if (this.datepickerRef) {
          const firstFocusableElement = this.datepickerRef
            .querySelector('button:not([disabled]), input:not([disabled])');
          firstFocusableElement.focus();
        }
      };
      this.setState({ disableDatepickerTabFocus: false });
      if (this.state.datepickerOpen) {
        onArrowDownClick();
      } else {
        this.onDatepickerOpen();
        setTimeout(() => {
          onArrowDownClick();
        }, this.timeoutDelay);
      }
    }
  }

  onDatepickerClickAway(event) {
    const startFieldProps = this.startFieldProps;
    const endFieldProps = this.endFieldProps;
    if (!this.justOpenedDatepicker
      && event.target !== this.startInputRef
      && event.target !== this.endInputRef) {
      this.onDatepickerClose();
      if (startFieldProps) {
        startFieldProps.input.onBlur();
      }
      if (endFieldProps) {
        endFieldProps.input.onBlur();
      }
      if (this.props.input) {
        this.props.input.onBlur();
      }
    }
  }

  onDatepickerBlur() {
    setTimeout(() => {
      const datepickerFocused = this.datepickerRef
        && isDescendant(this.datepickerRef, document.activeElement);
      if (!datepickerFocused) {
        this.onDatepickerClose();
      }
    }, this.timeoutDelay);
  }

  onDateChange(date) {
    const { input, variant, onDateSelect } = this.props;
    this.onDatepickerClose();
    input.onChange(date);
    if (variant === 'default' && this.startInputRef) {
      this.startInputRef.focus();
    }
    setTimeout(() => {
      onDateSelect(null, date);
    }, 100);
  }

  onStartDateChange(date) {
    this.startFieldProps.input.onChange(date);
    setTimeout(() => {
      const endFieldProps = this.endFieldProps;
      if (this.props.alwaysPrefillEndDate) {
        this.props.onStartDateSelect(null, {
          dateFrom: date,
          dateTo: date,
        });
        endFieldProps.input.onChange(date);
        this.setState({ focusedField: 'end' });
      } else if (endFieldProps.input.value
        && endFieldProps.meta.valid) {
        this.props.onStartDateSelect(null, {
          dateFrom: date,
          dateTo: endFieldProps.input.value,
        });
        this.onDatepickerClose();
      } else if (!endFieldProps.input.value) {
        this.props.onStartDateSelect(null, {
          dateFrom: date,
          dateTo: date,
        });
        endFieldProps.input.onChange(date);
        this.setState({ focusedField: 'end' });
      }
    }, this.timeoutDelay);
  }

  onEndDateChange(date) {
    const startFieldProps = this.startFieldProps;
    this.endFieldProps.input.onChange(date);
    this.endInputRef.focus();
    this.props.onEndDateSelect(null, {
      dateFrom: startFieldProps.input.value,
      dateTo: date,
    });
    if (startFieldProps.input.value
      && startFieldProps.meta.valid) {
      this.onDatepickerClose();
    } else {
      this.setState({ focusedField: 'start' });
    }
  }

  onNativeDefaultChange(event) {
    const { dateFormat, input, onDateSelect } = this.props;
    const nextValue = isoValueToDisplay(event.target.value, dateFormat);
    input.onChange(nextValue);
    setTimeout(() => {
      onDateSelect(null, nextValue);
    }, 100);
  }

  onNativeStartChange(event) {
    const { dateFormat, alwaysPrefillEndDate, onStartDateSelect } = this.props;
    const startValue = isoValueToDisplay(event.target.value, dateFormat);
    this.startFieldProps.input.onChange(startValue);
    setTimeout(() => {
      const endFieldProps = this.endFieldProps;
      if (alwaysPrefillEndDate) {
        onStartDateSelect(null, {
          dateFrom: startValue,
          dateTo: startValue,
        });
        endFieldProps.input.onChange(startValue);
        this.setState({ focusedField: 'end' });
      } else if (endFieldProps.input.value && endFieldProps.meta.valid) {
        onStartDateSelect(null, {
          dateFrom: startValue,
          dateTo: endFieldProps.input.value,
        });
      } else if (!endFieldProps.input.value) {
        onStartDateSelect(null, {
          dateFrom: startValue,
          dateTo: startValue,
        });
        endFieldProps.input.onChange(startValue);
        this.setState({ focusedField: 'end' });
      }
    }, this.timeoutDelay);
  }

  onNativeEndChange(event) {
    const { dateFormat, onEndDateSelect } = this.props;
    const endValue = isoValueToDisplay(event.target.value, dateFormat);
    const startFieldProps = this.startFieldProps;
    this.endFieldProps.input.onChange(endValue);
    onEndDateSelect(null, {
      dateFrom: startFieldProps.input.value,
      dateTo: endValue,
    });
  }

  onNativeDefaultBlur() {
    setTimeout(() => {
      this.onNativeDefaultChange({
        target: {
          value: this.nativeDefaultInputRef ? this.nativeDefaultInputRef.value : '',
        },
      });
    }, 0);
  }

  onNativeStartBlur() {
    setTimeout(() => {
      this.onNativeStartChange({
        target: {
          value: this.nativeStartInputRef ? this.nativeStartInputRef.value : '',
        },
      });
    }, 0);
  }

  onNativeEndBlur() {
    setTimeout(() => {
      this.onNativeEndChange({
        target: {
          value: this.nativeEndInputRef ? this.nativeEndInputRef.value : '',
        },
      });
    }, 0);
  }

  onClearButtonClick() {
    this.startFieldProps.input.onChange('');
    this.endFieldProps.input.onChange('');
  }

  get startFieldProps() {
    const { startDateName, ...otherProps } = this.props;
    return get(otherProps, startDateName);
  }

  get endFieldProps() {
    const { endDateName, ...otherProps } = this.props;
    return get(otherProps, endDateName);
  }

  render() {
    const {
      classes,
      dateFormat,
      minDate,
      maxDate,
      variant,
      startDateName,
      endDateName,
      startIcon,
      endIcon,
      size,
      onInputBlur,
      onStartInputBlur,
      onEndInputBlur,
      datepickerPlacement,
      inputAdornmentLgOnly,
      className,
      rangeButtons,
      labelOverlap,
      width,
      ...otherProps
    } = this.props;
    const isMobileLayout = width === 'xs' || width === 'sm';
    const { datepickerOpen, disableDatepickerTabFocus, focusedField } = this.state;
    let startFieldProps;
    let endFieldProps;
    if (variant === 'range') {
      startFieldProps = this.startFieldProps;
      endFieldProps = this.endFieldProps;
    }
    let verticalPlacement = 'bottom';
    let horizontalPlacement = otherProps.horizontalPlacement;
    const rootDomRect = this.rootRef ? this.rootRef.getBoundingClientRect() : null;
    const datepickerDomRect = this.datepickerRef
      ? this.datepickerRef.getBoundingClientRect() : null;
    const datepickerHeight = datepickerDomRect
      ? datepickerDomRect.height : 340;
    const datepickerWidth = datepickerDomRect
      ? datepickerDomRect.width : 333;
    if (rootDomRect) {
      if (((window.innerHeight - rootDomRect.top - rootDomRect.height)
        < datepickerHeight) && (datepickerHeight <= rootDomRect.top)) {
        verticalPlacement = 'top';
      }
      if (window.innerHeight === document.body.scrollHeight
        && ((window.innerHeight - rootDomRect.top - rootDomRect.height)
          < datepickerHeight)
        && (datepickerHeight >= rootDomRect.top)) {
        verticalPlacement = 'middle';
      }
      if (window.innerWidth - rootDomRect.left < datepickerWidth) {
        horizontalPlacement = 'right';
      }
    }
    const placement = datepickerPlacement || `${verticalPlacement}-${horizontalPlacement}`;
    const nativeMinDate = boundaryToIso(minDate, dateFormat);
    const nativeMaxDate = boundaryToIso(maxDate, dateFormat);
    const nativeMinMax = {
      ...(nativeMinDate ? { min: nativeMinDate } : {}),
      ...(nativeMaxDate ? { max: nativeMaxDate } : {}),
    };

    return (
      <div
        className={classNames(classes.root, className)}
        ref={(rootRef) => {
          this.rootRef = rootRef;
        }}
      >
        {variant === 'range' &&
          <FormControl
            role="group"
            className={classes.formControl}
          >
            {otherProps.label.length > 0 &&
              <FormLabel
                className={classNames({
                  [classes.labelOverlap]: labelOverlap === true,
                })}
                component="legend"
              >
                {otherProps.label}
              </FormLabel>
            }
            <div
              className={classNames(classes.textFields, {
                [classes.textFieldsColorLight]: otherProps.color === 'light',
              })}
            >
              <div className={classNames(classes.textField, {
                [classes.mobileNativeOverlayWrapper]: isMobileLayout,
              })}
              >
                <TextField
                  size={size}
                inputRootClassName={classes.textFieldInputRoot}
                {...otherProps}
                {...startFieldProps}
                id={startFieldProps.input.name}
                inputRef={(startInputRef) => {
                  this.startInputRef = startInputRef;
                }}
                input={{
                  ...startFieldProps.input,
                  onChange: this.onStartTextFieldChange,
                  onBlur: this.onStartTextFieldBlur,
                }}
                onFocus={this.onTextFieldFocus}
                onBlur={onStartInputBlur}
                onKeyDown={this.onTextFieldKeyDown}
                label=""
                startAdornment={
                  <InputAdornment
                    className={classNames({
                      [classes.inputAdornmentinputAdornmentLgOnly]: inputAdornmentLgOnly,
                    })}
                    position="start"
                  >
                    <IconCalendarStart
                      className={classNames(classes.iconCalendarStart, {
                        [classes.iconCalendarStartSizeSm]: size === 'sm',
                      })}
                    />
                  </InputAdornment>
                }
                  {...focusedField === 'start' && datepickerOpen ? { focused: true } : {}}
                />
                {isMobileLayout &&
                  <input
                    type="date"
                    className={classes.mobileNativeDateInputOverlay}
                    ref={(nativeStartInputRef) => {
                      this.nativeStartInputRef = nativeStartInputRef;
                    }}
                    value={displayValueToIso(startFieldProps.input.value, dateFormat)}
                    onInput={this.onNativeStartChange}
                    onChange={this.onNativeStartChange}
                    onChangeCapture={this.onNativeStartChange}
                    onKeyUp={this.onNativeStartChange}
                    onMouseUp={this.onNativeStartChange}
                    onTouchEnd={this.onNativeStartChange}
                    onBlur={this.onNativeStartBlur}
                    {...nativeMinMax}
                  />
                }
              </div>
              <div className={classNames(classes.textField, {
                [classes.mobileNativeOverlayWrapper]: isMobileLayout,
              })}
              >
                <TextField
                  size={size}
                inputRootClassName={classes.textFieldInputRoot}
                {...otherProps}
                {...endFieldProps}
                id={endFieldProps.input.name}
                inputRef={(endInputRef) => {
                  this.endInputRef = endInputRef;
                }}
                input={{
                  ...endFieldProps.input,
                  onChange: this.onEndTextFieldChange,
                  onBlur: this.onEndTextFieldBlur,
                }}
                onFocus={this.onTextFieldFocus}
                onBlur={onEndInputBlur}
                onKeyDown={this.onTextFieldKeyDown}
                label=""
                {...(endFieldProps.input.value && endFieldProps.input.value.length > 0)
                  || (startFieldProps.input.value && startFieldProps.input.value.length > 0) ? { endAdornment: (
                    <InputAdornment
                      className={classNames({
                        [classes.inputAdornmentinputAdornmentLgOnly]: inputAdornmentLgOnly,
                      })}
                      position="end"
                    >
                      <IconButton
                        className={classes.buttonClear}
                        onClick={this.onClearButtonClick}
                        tooltipContent="Clear Dates"
                        tabIndex={-1}
                      >
                        <IconCross className={classes.iconCross} />
                      </IconButton>
                    </InputAdornment>
                  ) } : {}}
                {...startFieldProps.meta.active
                    && startFieldProps.meta.invalid ? {
                    tabIndex: -1,
                  } : {}}
                  {...focusedField === 'end' && datepickerOpen ? { focused: true } : {}}
                />
                {isMobileLayout &&
                  <input
                    type="date"
                    className={classes.mobileNativeDateInputOverlay}
                    ref={(nativeEndInputRef) => {
                      this.nativeEndInputRef = nativeEndInputRef;
                    }}
                    value={displayValueToIso(endFieldProps.input.value, dateFormat)}
                    onInput={this.onNativeEndChange}
                    onChange={this.onNativeEndChange}
                    onChangeCapture={this.onNativeEndChange}
                    onKeyUp={this.onNativeEndChange}
                    onMouseUp={this.onNativeEndChange}
                    onTouchEnd={this.onNativeEndChange}
                    onBlur={this.onNativeEndBlur}
                    {...nativeMinMax}
                  />
                }
              </div>
            </div>
          </FormControl>
        }
        {variant === 'default' &&
          <div className={classNames({
            [classes.mobileNativeOverlayWrapper]: isMobileLayout,
          })}
          >
            <TextField
              size={size}
              inputRef={(startInputRef) => {
                this.startInputRef = startInputRef;
              }}
              {...startIcon ? {
                startAdornment: (
                  <InputAdornment
                    className={classNames({
                      [classes.inputAdornmentinputAdornmentLgOnly]: inputAdornmentLgOnly,
                    })}
                    position="start"
                  >
                    {startIcon === 'start' &&
                      <IconCalendarStart
                        className={classNames(classes.iconCalendarStart, {
                          [classes.iconCalendarStartSizeSm]: size === 'sm',
                        })}
                      />
                    }
                    {startIcon === 'end' &&
                      <IconCalendarEnd
                        className={classNames(classes.iconCalendarEnd, {
                          [classes.iconCalendarEndSizeSm]: size === 'sm',
                        })}
                      />
                    }
                  </InputAdornment>
                ),
              } : {}}
              {...endIcon ? {
                endAdornment: (
                  <InputAdornment
                    position="end"
                    className={classNames({
                      [classes.inputAdornmentinputAdornmentLgOnly]: inputAdornmentLgOnly,
                    })}
                  >
                    {endIcon === 'start' &&
                      <IconCalendarStart
                        className={classNames(classes.iconCalendarStart, {
                          [classes.iconCalendarStartSizeSm]: size === 'sm',
                        })}
                      />
                    }
                    {endIcon === 'end' &&
                      <IconCalendarEnd
                        className={classNames(classes.iconCalendarEnd, {
                          [classes.iconCalendarEndSizeSm]: size === 'sm',
                        })}
                      />
                    }
                  </InputAdornment>
                ),
              } : {}}
              {...otherProps}
              input={{
                ...otherProps.input,
                onChange: this.onTextFieldChange,
                onBlur: this.onTextFieldBlur,
              }}
              {...datepickerOpen ? { focused: true } : {}}
              onFocus={this.onTextFieldFocus}
              onBlur={onInputBlur}
              onKeyDown={this.onTextFieldKeyDown}
            />
            {isMobileLayout &&
              <input
                type="date"
                className={classes.mobileNativeDateInputOverlay}
                ref={(nativeDefaultInputRef) => {
                  this.nativeDefaultInputRef = nativeDefaultInputRef;
                }}
                value={displayValueToIso(otherProps.input.value, dateFormat)}
                onInput={this.onNativeDefaultChange}
                onChange={this.onNativeDefaultChange}
                onChangeCapture={this.onNativeDefaultChange}
                onKeyUp={this.onNativeDefaultChange}
                onMouseUp={this.onNativeDefaultChange}
                onTouchEnd={this.onNativeDefaultChange}
                onBlur={this.onNativeDefaultBlur}
                {...nativeMinMax}
              />
            }
          </div>
        }
        {datepickerOpen && !isMobileLayout &&
          <ClickAwayListener
            onClickAway={this.onDatepickerClickAway}
          >
            <RootRef
              rootRef={(datepickerRef) => {
                this.datepickerRef = datepickerRef;
              }}
            >
              <Datepicker
                range={variant === 'range'}
                {...variant === 'range' ? {
                  startDate: startFieldProps.input.value,
                  endDate: endFieldProps.input.value,
                  onStartDateChange: this.onStartDateChange,
                  onEndDateChange: this.onEndDateChange,
                  name: startFieldProps.input.name,
                  activeDate: startFieldProps.meta.active ? 'start' : 'end',
                } : {
                  date: otherProps.input.value,
                  onDateChange: this.onDateChange,
                  name: otherProps.input.name,
                }}
                dateFormat={dateFormat}
                minDate={minDate}
                maxDate={maxDate}
                onBlur={this.onDatepickerBlur}
                disableTabFocus={disableDatepickerTabFocus}
                placement={placement}
                rangeButtons={rangeButtons}
              />
            </RootRef>
          </ClickAwayListener>
        }
      </div>
    );
  }
}

DatepickerField.propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
  }),
  meta: PropTypes.shape({
    active: PropTypes.bool,
    valid: PropTypes.bool,
  }),
  dateFormat: PropTypes.string,
  minDate: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(moment),
  ]),
  maxDate: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(moment),
  ]),
  label: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'range']),
  startDateName: PropTypes.string,
  endDateName: PropTypes.string,
  startIcon: PropTypes.oneOf(['start', 'end']),
  endIcon: PropTypes.oneOf(['start', 'end']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  horizontalPlacement: PropTypes.oneOf(['left', 'right']),
  onInputFocus: PropTypes.func,
  onInputBlur: PropTypes.func,
  onStartInputBlur: PropTypes.func,
  onEndInputBlur: PropTypes.func,
  onDateSelect: PropTypes.func,
  onStartDateSelect: PropTypes.func,
  onEndDateSelect: PropTypes.func,
  datepickerPlacement: PropTypes.string,
  alwaysPrefillEndDate: PropTypes.bool,
  inputAdornmentLgOnly: PropTypes.bool,
  className: PropTypes.string,
  rangeButtons: PropTypes.bool,
  width: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    formControl: PropTypes.string.isRequired,
    popperRoot: PropTypes.string.isRequired,
    textFields: PropTypes.string.isRequired,
    textField: PropTypes.string.isRequired,
    textFieldInputRoot: PropTypes.string.isRequired,
    inputAdornmentinputAdornmentLgOnly: PropTypes.string.isRequired,
    iconCalendarStart: PropTypes.string.isRequired,
    iconCalendarStartSizeSm: PropTypes.string.isRequired,
    iconCalendarEnd: PropTypes.string.isRequired,
    iconCalendarEndSizeSm: PropTypes.string.isRequired,
    mobileNativeOverlayWrapper: PropTypes.string.isRequired,
    mobileNativeDateInputOverlay: PropTypes.string.isRequired,
    labelOverlap: PropTypes.string.isRequired,
  }).isRequired,
};

DatepickerField.defaultProps = {
  input: null,
  meta: null,
  variant: 'default',
  startDateName: null,
  endDateName: null,
  label: '',
  dateFormat: defaultDateFormat,
  minDate: '1900-01-01',
  maxDate: '2100-01-01',
  startIcon: null,
  endIcon: null,
  size: 'md',
  horizontalPlacement: 'left',
  onInputFocus: () => {},
  onInputBlur: () => {},
  onStartInputBlur: () => {},
  onEndInputBlur: () => {},
  onDateSelect: () => {},
  onStartDateSelect: () => {},
  onEndDateSelect: () => {},
  datepickerPlacement: null,
  alwaysPrefillEndDate: false,
  inputAdornmentLgOnly: false,
  className: '',
  rangeButtons: false,
};

export default withWidth()(withStyles(styles)(DatepickerField));



// WEBPACK FOOTER //
// ./src/components/fields/DatepickerField/DatepickerField.js